import type { Axios } from 'axios'

import type {
  AddGrantProgramPayload,
  AdminCandidateResponse,
  CreateStandaloneFormQuestionPayload,
  CreateStandaloneFormQuestionResponse,
  UpdateStandaloneFormQuestionPayload,
  StandaloneFormQuestionsResponse,
  StandaloneFormQuestionDetailResponse,
  ProgramsResponse,
  AutoAssignmentRulesResponse,
  CandidateTasksResponse,
  CreateTaskTemplatePayload,
  CreateTaskTemplateResponse,
  DeleteTaskTemplateParams,
  FormDetailsResponse,
  FormsListResponse,
  FormsResponse,
  GetStandaloneFormQuestionsParams,
  GetFormsParams,
  GetFormsListParams,
  GetProgramCandidatesParams,
  CreateFormPayload,
  CreateFormResponse,
  UpdateFormPayload,
  GetProgramParams,
  GetProgramsParams,
  PatchAssignmentRulePayload,
  PostAssignmentRulePayload,
  Program,
  ProgramCandidatesResponse,
  ProgramResponse,
  StateMachineMetadataResponse,
  TaskAssigneesResponse,
  TaskTemplatesResponse,
  UpdateGrantProgramPayload,
  UpdateProgramCandidateParams,
  UpdateTaskAssigneePayload,
  UpdateTaskStatusPayload,
  UpdateTaskTemplateResponse
} from './grantProgram.types'

export const grantProgramService = ({ percentClient }: { percentClient: Axios }) => ({
  getAllPrograms: (params?: GetProgramsParams) =>
    percentClient.get<ProgramsResponse>(`v1/admin/grants/programs`, {
      params
    }),
  addProgram: (payload: AddGrantProgramPayload) =>
    percentClient.post<ProgramResponse>(`v1/admin/grants/programs`, payload),
  updateProgram: ({ programId, payload }: { programId: string; payload: UpdateGrantProgramPayload }) =>
    percentClient.patch<ProgramResponse>(`v1/admin/grants/programs/${programId}`, payload),
  getProgramById: ({ programId }: GetProgramParams) =>
    percentClient.get<Program>(`v1/admin/grants/programs/${programId}`),

  getPartnerTaskTemplates: ({ partnerId }: { partnerId: string }) =>
    percentClient.get<TaskTemplatesResponse>(`v1/admin/grants/${partnerId}/task-templates`),
  getTaskTemplates: ({ programId }: { programId: string }) =>
    percentClient.get<TaskTemplatesResponse>(`v1/admin/grants/programs/${programId}/task-templates`),
  createTaskTemplate: ({ grantProgramId, payload }: { grantProgramId: string; payload: CreateTaskTemplatePayload }) =>
    percentClient.post<CreateTaskTemplateResponse>(
      `v1/admin/grants/programs/${grantProgramId}/task-templates`,
      payload
    ),
  updateTaskTemplate: ({
    grantProgramId,
    taskTemplateId,
    payload
  }: {
    grantProgramId: string
    taskTemplateId: string
    payload: Partial<CreateTaskTemplatePayload>
  }) =>
    percentClient.patch<UpdateTaskTemplateResponse>(
      `v1/admin/grants/programs/${grantProgramId}/task-templates/${taskTemplateId}`,
      payload
    ),
  deleteTaskTemplate: ({ grantProgramId, taskTemplateId }: DeleteTaskTemplateParams) =>
    percentClient.delete(`v1/admin/grants/programs/${grantProgramId}/task-templates/${taskTemplateId}`),

  getForms: (params?: GetFormsParams) => percentClient.get<FormsResponse>(`v1/admin/grants/forms`, { params }),
  getFormById: ({ formId }: { formId: string }) =>
    percentClient.get<FormDetailsResponse>(`v1/admin/grants/forms/${formId}`),
  createForm: (payload: CreateFormPayload) => percentClient.post<CreateFormResponse>(`v2/admin/grants/forms`, payload),
  updateForm: ({ formId, payload }: { formId: string; payload: UpdateFormPayload }) =>
    percentClient.patch(`v2/admin/grants/forms/${formId}`, payload),
  getFormsList: (params?: GetFormsListParams) =>
    percentClient.get<FormsListResponse>(`v1/admin/grants/forms`, { params }),

  addAutoAssignmentRule: (payload: PostAssignmentRulePayload) =>
    percentClient.post(`v1/admin/grants/tasks-auto-assignment-rules`, payload),
  updateAutoAssignmentRule: ({ id, payload }: { id: string; payload: PatchAssignmentRulePayload }) =>
    percentClient.patch(`v1/admin/grants/tasks-auto-assignment-rules/${id}`, payload),
  deleteAutoAssignmentRule: ({ id }: { id: string }) =>
    percentClient.delete(`v1/admin/grants/tasks-auto-assignment-rules/${id}`),
  getAutoAssignmentRules: () =>
    percentClient.get<AutoAssignmentRulesResponse>(`v1/admin/grants/tasks-auto-assignment-rules`),

  getProgramCandidates: (params: GetProgramCandidatesParams) => {
    const { programId, ...queryParams } = params

    return percentClient.get<ProgramCandidatesResponse>(`v1/admin/grants/programs/${programId}/candidates`, {
      params: queryParams
    })
  },
  recalculateProgram: ({ programId }: { programId: string }) =>
    percentClient.post(`v1/admin/grants/programs/${programId}/candidates/recalculate-states`),
  recalculateProgramCandidate: ({ programId, candidateId }: { programId: string; candidateId: string }) =>
    percentClient.post(`v1/admin/grants/programs/${programId}/candidates/${candidateId}/recalculate-state`),
  updateProgramCandidate: ({
    programId,
    candidateId,
    payload
  }: {
    programId: string
    candidateId: string
    payload: UpdateProgramCandidateParams
  }) => percentClient.patch(`v1/admin/grants/programs/${programId}/candidates/${candidateId}`, payload),
  getProgramCandidate: ({ candidateId }: { candidateId: string }) =>
    percentClient.get<AdminCandidateResponse>(`v1/admin/grants/candidates/${candidateId}`),
  rejectProgramCandidate: ({
    programId,
    candidateId,
    rejectionReason
  }: {
    programId: string
    candidateId: string
    rejectionReason?: string
  }) =>
    percentClient.post(`v1/admin/grants/programs/${programId}/candidates/${candidateId}/reject`, {
      ...(rejectionReason && { rejectionReason })
    }),
  deleteProgramCandidate: ({ candidateId }: { candidateId: string }) =>
    percentClient.delete(`v1/admin/grants/candidates/${candidateId}`),
  getCandidateTasks: ({ candidateId }: { candidateId: string }) =>
    percentClient.get<CandidateTasksResponse>(`v1/admin/grants/candidates/${candidateId}/tasks`),
  updateTaskStatus: ({ taskId, payload }: { taskId: string; payload: UpdateTaskStatusPayload }) =>
    percentClient.patch(`v1/admin/grants/tasks/${taskId}`, payload),
  getTaskAssignees: ({ programId }: { programId: string }) =>
    percentClient.get<TaskAssigneesResponse>(`v1/admin/grants/programs/${programId}/task-assignees`),
  updateTaskAssignee: ({ programId, payload }: { programId: string; payload: UpdateTaskAssigneePayload }) =>
    percentClient.put(`v1/admin/grants/programs/${programId}/task-assignees`, payload),
  getStateMachineMetadata: ({ programId }: { programId: string }) =>
    percentClient.get<StateMachineMetadataResponse>(`v1/admin/grants/programs/${programId}/state-machine/metadata`),

  getStandaloneFormQuestions: (params?: GetStandaloneFormQuestionsParams) =>
    percentClient.get<StandaloneFormQuestionsResponse>(`v1/admin/grants/questions`, { params }),

  getStandaloneFormQuestion: ({ questionId, locale }: { questionId: string; locale?: string }) =>
    percentClient.get<StandaloneFormQuestionDetailResponse>(`v1/admin/grants/questions/${questionId}`, {
      params: { locale }
    }),

  createStandaloneFormQuestion: (payload: CreateStandaloneFormQuestionPayload) =>
    percentClient.post<CreateStandaloneFormQuestionResponse>(`v1/admin/grants/questions`, payload),

  updateStandaloneFormQuestion: ({
    questionId,
    payload
  }: {
    questionId: string
    payload: UpdateStandaloneFormQuestionPayload
  }) => percentClient.patch(`v1/admin/grants/questions/${questionId}`, payload)
})
